import {
  WbHeaderActionButton,
  WittyChat,
  WittyIcon,
} from '@agilelab/plugin-wb-platform';
import { Dialog, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { useEffect } from 'react';
import {
  useWittyAssistant,
  WittyAssistantHook,
} from '../../contexts/useWittyAssistant';
import { useWittyAssistantContext } from '../../contexts/WittyAssistantContext';
import { useWittyAssistantEnabled } from '../../hooks/useWittyAssistantEnabled';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  expandingChat: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: theme.zIndex.drawer - 1,
    justifySelf: 'flex-end',
    height: '100vh',
    width: '40vw',
  },
  expandedChat: {
    position: 'absolute',
    top: 0,
    right: 0,
    justifySelf: 'flex-end',
    zIndex: theme.zIndex.drawer - 1,
    height: '100vh',
    width: '70vw',
  },
}));

const WittyAssistantContent = ({
  assistant,
}: {
  assistant: WittyAssistantHook;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { enabled, loading: loadingEnabled } = useWittyAssistantEnabled();
  const mobile = useMediaQuery(theme.breakpoints.down('xs'));

  const {
    loading,
    generating,
    statusText,
    activeToolCalls,
    unavailableMcpServers,
    onOpenMcpConnection,
    messages,
    open,
    onNewMessage,
    onStop,
    setOpen,
    expanded,
    setExpanded,
    onNewSession,
    historyOpen,
    setHistoryOpen,
    sessions,
    sessionsLoading,
    loadSessions,
    loadMoreSessions,
    hasMoreSessions,
    searchSessions,
    sessionSearchQuery,
    onSelectSession,
    onRenameSession,
    onDeleteSession,
    activeSessionId,
    agents,
    selectedAgentId,
    setSelectedAgentId,
  } = assistant;

  useEffect(() => {
    if (!loadingEnabled && !enabled && open) {
      setOpen(false);
      setExpanded(false);
    }
  }, [enabled, loadingEnabled, open, setExpanded, setOpen]);

  if (loadingEnabled || !enabled) {
    return null;
  }

  const chatContent = (
    <WittyChat
      mobile={mobile}
      incomingMessage={loading}
      responseInProgress={generating}
      statusText={statusText}
      activeToolCalls={activeToolCalls}
      unavailableMcpServers={unavailableMcpServers}
      onOpenMcpConnection={onOpenMcpConnection}
      messages={messages}
      onNewMessage={onNewMessage}
      onStop={generating ? onStop : undefined}
      onNewSession={onNewSession}
      setOpen={setOpen}
      setChatExpanded={() =>
        setExpanded((prevExpanded: boolean) => !prevExpanded)
      }
      isMicrophoneActive={false}
      isAttachContentActive={false}
      isFeedbackActive={false}
      historyOpen={historyOpen}
      setHistoryOpen={setHistoryOpen}
      sessions={sessions}
      sessionsLoading={sessionsLoading}
      loadSessions={loadSessions}
      loadMoreSessions={loadMoreSessions}
      hasMoreSessions={hasMoreSessions}
      searchSessions={searchSessions}
      sessionSearchQuery={sessionSearchQuery}
      onSelectSession={onSelectSession}
      onRenameSession={onRenameSession}
      onDeleteSession={onDeleteSession}
      activeSessionId={activeSessionId}
      agents={agents}
      selectedAgentId={selectedAgentId}
      onSelectAgent={setSelectedAgentId}
    />
  );

  const trigger = (
    <WbHeaderActionButton
      icon={<WittyIcon />}
      label="Ask Witty"
      aria-label="Open Witty Chat"
      onClick={() => {
        setExpanded(false);
        setOpen(true);
      }}
    />
  );

  if (mobile)
    return (
      <>
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          fullScreen
          aria-label="Witty Chat"
        >
          {chatContent}
        </Dialog>
        {trigger}
      </>
    );

  return open ? (
    <div
      className={clsx(expanded ? classes.expandedChat : classes.expandingChat)}
    >
      {chatContent}
    </div>
  ) : (
    trigger
  );
};

const StandaloneWittyAssistant = () => (
  <WittyAssistantContent assistant={useWittyAssistant()} />
);

export const WittyAssistant = () => {
  const assistant = useWittyAssistantContext();

  return assistant ? (
    <WittyAssistantContent assistant={assistant} />
  ) : (
    <StandaloneWittyAssistant />
  );
};
